import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useAppDispatch, useAppSelector } from 'universal-scripts'
import {
  clearSelection,
  addDataset,
  addNode,
  replaceNode,
  wrapNode,
  removeNode,
  applyJoinNode
} from 'src/store/slices/query'
import type { BlueprintNode } from 'src/types/queries'
import type { DataItemPayload } from 'src/types/global'
import { isOperationValid } from 'src/utils/blueprintUtils'
import DataSetEditor from './DataSetEditor/DataSetEditor'
import OperationEditor from './OperationEditor/OperationEditor'
import { OPERATION_TYPES } from 'src/constants/queries'

import './NodeEditor.css'

export default function NodeEditor() {
  const { selectedNodeId, isAddingNode, addingOperationOver } = useAppSelector(
    (state) => state.query.ui
  )
  const blueprint = useAppSelector((state) => state.query.draft.blueprint || [])

  const isRowRoot =
    selectedNodeId != null && blueprint.some((n) => n.nodeId === selectedNodeId)

  if (!isAddingNode && !addingOperationOver && !(selectedNodeId && isRowRoot))
    return null

  const editorKey = selectedNodeId
    ? `edit-${selectedNodeId}`
    : addingOperationOver
      ? `add-op-${addingOperationOver}`
      : isAddingNode
        ? 'add-dataset'
        : 'idle'

  return <NodeEditorInner key={editorKey} />
}

function NodeEditorInner() {
  const intl = useIntl()
  const dispatch = useAppDispatch()

  const { selectedNodeId, isAddingNode, addingOperationOver } = useAppSelector(
    (state) => state.query.ui
  )
  const blueprint = useAppSelector((state) => state.query.draft.blueprint || [])

  const activeNode = blueprint.find((node) => node.nodeId === selectedNodeId)
  const nodeToWrap = blueprint.find(
    (node) => node.nodeId === addingOperationOver
  )

  const isAddingOperation = addingOperationOver !== null
  const isEditingDataset = activeNode?.type === 'dataset'
  const isEditingOperation =
    activeNode &&
    OPERATION_TYPES.includes(
      activeNode.type as (typeof OPERATION_TYPES)[number]
    )

  const [tempDatasetId, setTempDatasetId] = useState<string>(() =>
    activeNode?.type === 'dataset' ? activeNode.id : ''
  )
  const [reportedOperationNode, setReportedOperationNode] =
    useState<BlueprintNode | null>(null)

  const handleSelectDataset = (item: DataItemPayload) => {
    setTempDatasetId(item.id)
    dispatch(addDataset({ id: item.id, title: item.name }))
  }

  const handleApply = () => {
    if (
      isAddingOperation &&
      reportedOperationNode &&
      nodeToWrap &&
      isOperationValid(reportedOperationNode)
    ) {
      if (reportedOperationNode.type === 'join') {
        dispatch(
          applyJoinNode({
            leftNodeId: nodeToWrap.nodeId,
            joinNode: reportedOperationNode
          })
        )
      } else {
        dispatch(
          wrapNode({
            nodeId: nodeToWrap.nodeId,
            newNode: reportedOperationNode
          })
        )
      }
    } else if (
      isEditingOperation &&
      reportedOperationNode &&
      activeNode &&
      isOperationValid(reportedOperationNode)
    ) {
      dispatch(
        replaceNode({
          nodeId: activeNode.nodeId,
          newNode: reportedOperationNode
        })
      )
    } else if (isAddingNode && tempDatasetId) {
      const newNode: BlueprintNode = {
        nodeId: crypto.randomUUID(),
        type: 'dataset',
        id: tempDatasetId
      }
      dispatch(addNode(newNode))
    } else if (isEditingDataset && activeNode && tempDatasetId) {
      const newNode: BlueprintNode = {
        nodeId: activeNode.nodeId,
        type: 'dataset',
        id: tempDatasetId
      }
      dispatch(replaceNode({ nodeId: activeNode.nodeId, newNode }))
    }

    dispatch(clearSelection())
  }

  const handleDelete = () => {
    if (activeNode) {
      const isConfirmed = window.confirm(
        intl.formatMessage({ id: 'node-editor.confirm.delete' })
      )
      if (isConfirmed) {
        dispatch(removeNode(activeNode.nodeId))
        dispatch(clearSelection())
      }
    }
  }

  const handleClose = () => {
    dispatch(clearSelection())
  }

  const headerKey = isAddingOperation
    ? 'node-editor.title.add-operation'
    : isAddingNode
      ? 'node-editor.title.add'
      : isEditingOperation
        ? 'node-editor.title.edit-operation'
        : 'node-editor.title.edit'

  const isApplyDisabled = (() => {
    if (isAddingOperation || isEditingOperation) {
      return !reportedOperationNode || !isOperationValid(reportedOperationNode)
    }
    return !tempDatasetId
  })()

  const isEditing = activeNode !== undefined

  return (
    <div id="node-editor-panel" onClick={(e) => e.stopPropagation()}>
      <header className="node-editor-header">
        <h3>
          <FormattedMessage id={headerKey} />
        </h3>
        <div className="node-edition-buttons">
          <button
            className="highlight-button"
            onClick={handleApply}
            disabled={isApplyDisabled}
          >
            <span>
              <FormattedMessage id="node-editor.button.apply" />
            </span>
          </button>
          {isEditing && (
            <button className="standart-button" onClick={handleDelete}>
              <span className="icon icon-bin" />
              <span>
                <FormattedMessage id="delete" />
              </span>
            </button>
          )}
          <span
            className="icon icon-cross standart-button"
            onClick={handleClose}
          />
        </div>
      </header>
      {isAddingOperation || isEditingOperation ? (
        <OperationEditor
          mode={isEditingOperation ? 'edit' : 'add'}
          initialNode={isEditingOperation ? activeNode : null}
          nodeToWrap={nodeToWrap ?? null}
          onChangeNode={setReportedOperationNode}
        />
      ) : (
        <DataSetEditor
          selectedId={tempDatasetId || undefined}
          onSelect={handleSelectDataset}
        />
      )}
    </div>
  )
}
